<script>
import { useAlert } from 'dashboard/composables';
import inboxMixin from 'shared/mixins/inboxMixin';
import SettingsSection from '../../../../../components/SettingsSection.vue';
import ImapSettings from '../ImapSettings.vue';
import SmtpSettings from '../SmtpSettings.vue';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import TextArea from 'next/textarea/TextArea.vue';
import WhatsappReauthorize from '../channels/whatsapp/Reauthorize.vue';
import InboxReconnectionRequired from '../components/InboxReconnectionRequired.vue';
import { sanitizeAllowedDomains } from 'dashboard/helper/URLHelper';

export default {
  components: {
    SettingsSection,
    ImapSettings,
    SmtpSettings,
    NextButton,
    NextInput,
    TextArea,
    WhatsappReauthorize,
    InboxReconnectionRequired,
  },
  mixins: [inboxMixin],
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      hmacMandatory: false,
      whatsAppInboxAPIKey: '',
      isRequestingReauthorization: false,
      isSyncingTemplates: false,
      allowedDomains: '',
      isUpdatingAllowedDomains: false,
      telegramUserReauthStep: 'idle',
      telegramUserLoginSessionId: '',
      telegramUserPhoneNumberMasked: '',
      telegramUserCode: '',
      telegramUserCodeError: '',
      telegramUserPassword: '',
      telegramUserPasswordError: '',
      telegramUserEgressBindIp: '',
      telegramUserEgressBindIpError: '',
    };
  },
  validations: {
    whatsAppInboxAPIKey: { required },
  },
  computed: {
    uiFlags() {
      return this.$store.getters['inboxes/getUIFlags'] || {};
    },
    isEmbeddedSignupWhatsApp() {
      return this.inbox.provider_config?.source === 'embedded_signup';
    },
    whatsappAppId() {
      return window.chatwootConfig?.whatsappAppId;
    },
    isForwardingEnabled() {
      return !!this.inbox.forwarding_enabled;
    },
    isTelegramUserReconnectionRequired() {
      return ['disconnected', 'error'].includes(
        this.inbox.telegram_user_inbox_status
      );
    },
    telegramUserInboxStatusLabel() {
      const status = this.inbox.telegram_user_inbox_status;
      const keyMap = {
        connecting:
          'INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_USER_INBOX.STATUS.CONNECTING',
        connected:
          'INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_USER_INBOX.STATUS.CONNECTED',
        disconnected:
          'INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_USER_INBOX.STATUS.DISCONNECTED',
        error: 'INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_USER_INBOX.STATUS.ERROR',
      };

      return this.$t(
        keyMap[status] ||
          'INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_USER_INBOX.STATUS.UNKNOWN'
      );
    },
  },
  watch: {
    inbox() {
      this.setDefaults();
    },
  },
  mounted() {
    this.setDefaults();
  },
  methods: {
    setDefaults() {
      this.hmacMandatory = this.inbox.hmac_mandatory || false;
      this.allowedDomains = this.inbox.allowed_domains || '';
      this.telegramUserEgressBindIp = this.inbox.egress_bind_ip || '';
    },
    handleHmacFlag() {
      this.updateInbox();
    },
    async updateInbox() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            hmac_mandatory: this.hmacMandatory,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async updateAllowedDomains() {
      this.isUpdatingAllowedDomains = true;
      const sanitizedAllowedDomains = sanitizeAllowedDomains(
        this.allowedDomains
      );
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            allowed_domains: sanitizedAllowedDomains,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        this.allowedDomains = sanitizedAllowedDomains;
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.isUpdatingAllowedDomains = false;
      }
    },
    async updateWhatsAppInboxAPIKey() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {},
        };

        payload.channel.provider_config = {
          ...this.inbox.provider_config,
          api_key: this.whatsAppInboxAPIKey,
        };

        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async handleReconfigure() {
      if (this.$refs.whatsappReauth) {
        await this.$refs.whatsappReauth.requestAuthorization();
      }
    },
    async syncTemplates() {
      this.isSyncingTemplates = true;
      try {
        await this.$store.dispatch('inboxes/syncTemplates', this.inbox.id);
        useAlert(
          this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_SUCCESS')
        );
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.isSyncingTemplates = false;
      }
    },
    async startTelegramUserReauthorization() {
      try {
        this.telegramUserCodeError = '';
        this.telegramUserPasswordError = '';
        this.telegramUserPassword = '';
        const response = await this.$store.dispatch(
          'inboxes/startTelegramUserLogin',
          { inboxId: this.inbox.id }
        );

        this.telegramUserLoginSessionId = response.login_session_id;
        this.telegramUserPhoneNumberMasked = response.phone_number_masked;
        this.telegramUserReauthStep = 'code';
      } catch (error) {
        useAlert(
          error.message ||
            this.$t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.API.ERROR_MESSAGE')
        );
      }
    },
    async verifyTelegramUserReauthorizationCode() {
      if (!this.telegramUserCode) {
        this.telegramUserCodeError = this.$t(
          'INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.CODE.ERROR'
        );
        return;
      }

      try {
        this.telegramUserCodeError = '';
        const response = await this.$store.dispatch(
          'inboxes/verifyTelegramUserLoginCode',
          {
            inboxId: this.inbox.id,
            loginSessionId: this.telegramUserLoginSessionId,
            code: this.telegramUserCode,
          }
        );

        if (response?.next_step === 'LOGIN_NEXT_STEP_ENTER_PASSWORD') {
          this.telegramUserReauthStep = 'password';
        } else {
          await this.finishTelegramUserReauthorization();
        }
      } catch (error) {
        useAlert(
          error.message ||
            this.$t(
              'INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.VERIFY_API.ERROR_MESSAGE'
            )
        );
      }
    },
    async verifyTelegramUserReauthorizationPassword() {
      if (!this.telegramUserPassword) {
        this.telegramUserPasswordError = this.$t(
          'INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.PASSWORD.ERROR'
        );
        return;
      }

      try {
        this.telegramUserPasswordError = '';
        await this.$store.dispatch('inboxes/verifyTelegramUserLoginPassword', {
          inboxId: this.inbox.id,
          loginSessionId: this.telegramUserLoginSessionId,
          password: this.telegramUserPassword,
        });
        await this.finishTelegramUserReauthorization();
      } catch (error) {
        useAlert(
          error.message ||
            this.$t(
              'INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.VERIFY_API.ERROR_MESSAGE'
            )
        );
      }
    },
    async finishTelegramUserReauthorization() {
      await this.$store.dispatch('inboxes/get');

      this.telegramUserReauthStep = 'idle';
      this.telegramUserLoginSessionId = '';
      this.telegramUserPhoneNumberMasked = '';
      this.telegramUserCode = '';
      this.telegramUserPassword = '';
    },
    async updateTelegramUserEgressBindIp() {
      const ipv4Regex =
        /^(25[0-5]|2[0-4]\d|1\d{2}|[1-9]\d|\d)(\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]\d|\d)){3}$/;
      if (!this.telegramUserEgressBindIp) {
        this.telegramUserEgressBindIpError = this.$t(
          'INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_USER_INBOX.EGRESS_BIND_IP.ERROR'
        );
        return;
      }
      if (!ipv4Regex.test(this.telegramUserEgressBindIp)) {
        this.telegramUserEgressBindIpError = this.$t(
          'INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_USER_INBOX.EGRESS_BIND_IP.ERROR'
        );
        return;
      }
      this.telegramUserEgressBindIpError = '';
      try {
        await this.$store.dispatch('inboxes/updateInbox', {
          id: this.inbox.id,
          formData: false,
          channel: { egress_bind_ip: this.telegramUserEgressBindIp },
        });
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(error.message || this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
  },
};
</script>

<template>
  <div v-if="isATwilioChannel" class="mx-8">
    <SettingsSection
      :title="$t('INBOX_MGMT.ADD.TWILIO.API_CALLBACK.TITLE')"
      :sub-title="$t('INBOX_MGMT.ADD.TWILIO.API_CALLBACK.SUBTITLE')"
    >
      <woot-code :script="inbox.callback_webhook_url" lang="html" />
    </SettingsSection>
    <SettingsSection
      v-if="isATwilioWhatsAppChannel"
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_TITLE')"
      :sub-title="
        $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_SUBHEADER')
      "
    >
      <div class="flex justify-start items-center mt-2">
        <NextButton :disabled="isSyncingTemplates" @click="syncTemplates">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_BUTTON') }}
        </NextButton>
      </div>
    </SettingsSection>
  </div>
  <div v-else-if="isAVoiceChannel" class="mx-8">
    <SettingsSection
      :title="$t('INBOX_MGMT.ADD.VOICE.CONFIGURATION.TWILIO_VOICE_URL_TITLE')"
      :sub-title="
        $t('INBOX_MGMT.ADD.VOICE.CONFIGURATION.TWILIO_VOICE_URL_SUBTITLE')
      "
    >
      <woot-code :script="inbox.voice_call_webhook_url" lang="html" />
    </SettingsSection>
    <SettingsSection
      :title="$t('INBOX_MGMT.ADD.VOICE.CONFIGURATION.TWILIO_STATUS_URL_TITLE')"
      :sub-title="
        $t('INBOX_MGMT.ADD.VOICE.CONFIGURATION.TWILIO_STATUS_URL_SUBTITLE')
      "
    >
      <woot-code :script="inbox.voice_status_webhook_url" lang="html" />
    </SettingsSection>
  </div>

  <div v-else-if="isALineChannel" class="mx-8">
    <SettingsSection
      :title="$t('INBOX_MGMT.ADD.LINE_CHANNEL.API_CALLBACK.TITLE')"
      :sub-title="$t('INBOX_MGMT.ADD.LINE_CHANNEL.API_CALLBACK.SUBTITLE')"
    >
      <woot-code :script="inbox.callback_webhook_url" lang="html" />
    </SettingsSection>
  </div>
  <div v-else-if="isAWebWidgetInbox">
    <div class="mx-8">
      <SettingsSection
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.MESSENGER_HEADING')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.MESSENGER_SUB_HEAD')"
      >
        <woot-code
          :script="inbox.web_widget_script"
          lang="html"
          :codepen-title="`${inbox.name} - Chatwoot Widget Test`"
          enable-code-pen
        />
      </SettingsSection>

      <SettingsSection
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.ALLOWED_DOMAINS.TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.ALLOWED_DOMAINS.SUBTITLE')"
      >
        <div class="flex flex-col w-full max-w-3xl gap-4">
          <TextArea
            v-model="allowedDomains"
            :placeholder="
              $t('INBOX_MGMT.SETTINGS_POPUP.ALLOWED_DOMAINS.PLACEHOLDER')
            "
            auto-height
            min-height="8rem"
            class="w-full"
          />
          <div>
            <NextButton
              :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
              :is-loading="isUpdatingAllowedDomains"
              @click="updateAllowedDomains"
            />
          </div>
        </div>
      </SettingsSection>

      <SettingsSection
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_VERIFICATION')"
      >
        <woot-code :script="inbox.hmac_token" />
        <template #subTitle>
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.HMAC_DESCRIPTION') }}
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="https://www.chatwoot.com/docs/product/channels/live-chat/sdk/identity-validation/"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.HMAC_LINK_TO_DOCS') }}
          </a>
        </template>
      </SettingsSection>
      <SettingsSection
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_VERIFICATION')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_DESCRIPTION')"
      >
        <div class="flex gap-2 items-center">
          <input
            id="hmacMandatory"
            v-model="hmacMandatory"
            type="checkbox"
            @change="handleHmacFlag"
          />
          <label for="hmacMandatory">
            {{ $t('INBOX_MGMT.EDIT.ENABLE_HMAC.LABEL') }}
          </label>
        </div>
      </SettingsSection>
    </div>
  </div>
  <div v-else-if="isAPIInbox" class="mx-8">
    <SettingsSection
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_IDENTIFIER')"
      :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_IDENTIFIER_SUB_TEXT')"
    >
      <woot-code :script="inbox.inbox_identifier" />
    </SettingsSection>

    <SettingsSection
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_VERIFICATION')"
      :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_DESCRIPTION')"
    >
      <woot-code :script="inbox.hmac_token" />
    </SettingsSection>
    <SettingsSection
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_VERIFICATION')"
      :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_DESCRIPTION')"
    >
      <div class="flex gap-2 items-center">
        <input
          id="hmacMandatory"
          v-model="hmacMandatory"
          type="checkbox"
          @change="handleHmacFlag"
        />
        <label for="hmacMandatory">
          {{ $t('INBOX_MGMT.EDIT.ENABLE_HMAC.LABEL') }}
        </label>
      </div>
    </SettingsSection>
  </div>
  <div v-else-if="isATelegramUserInbox" class="mx-8">
    <InboxReconnectionRequired
      v-if="
        isTelegramUserReconnectionRequired && telegramUserReauthStep === 'idle'
      "
      class="mb-4"
      @reauthorize="startTelegramUserReauthorization"
    />
    <SettingsSection
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_USER_INBOX.TITLE')"
    >
      <div class="flex flex-col w-full max-w-3xl gap-3 text-sm">
        <div class="flex items-center justify-between gap-4">
          <span class="text-n-slate-11">
            {{
              $t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_USER_INBOX.STATUS_LABEL')
            }}
          </span>
          <span class="text-n-slate-12">
            {{ telegramUserInboxStatusLabel }}
          </span>
        </div>

        <div
          v-if="inbox.telegram_display_name"
          class="flex items-center justify-between gap-4"
        >
          <span class="text-n-slate-11">
            {{
              $t(
                'INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_USER_INBOX.DISPLAY_NAME_LABEL'
              )
            }}
          </span>
          <span class="text-n-slate-12">{{ inbox.telegram_display_name }}</span>
        </div>

        <div
          v-if="inbox.telegram_username"
          class="flex items-center justify-between gap-4"
        >
          <span class="text-n-slate-11">
            {{
              $t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_USER_INBOX.USERNAME_LABEL')
            }}
          </span>
          <span class="text-n-slate-12">{{ inbox.telegram_username }}</span>
        </div>

        <div
          v-if="inbox.telegram_user_id"
          class="flex items-center justify-between gap-4"
        >
          <span class="text-n-slate-11">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_USER_INBOX.ID_LABEL') }}
          </span>
          <span class="text-n-slate-12">{{ inbox.telegram_user_id }}</span>
        </div>

        <div
          v-if="inbox.telegram_user_inbox_last_connected_at"
          class="flex items-center justify-between gap-4"
        >
          <span class="text-n-slate-11">
            {{
              $t(
                'INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_USER_INBOX.LAST_CONNECTED_AT_LABEL'
              )
            }}
          </span>
          <span class="text-n-slate-12">
            {{ inbox.telegram_user_inbox_last_connected_at }}
          </span>
        </div>

        <div
          v-if="inbox.telegram_user_inbox_last_error"
          class="flex items-start justify-between gap-4"
        >
          <span class="text-n-slate-11">
            {{
              $t(
                'INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_USER_INBOX.LAST_ERROR_LABEL'
              )
            }}
          </span>
          <span class="text-n-slate-12 text-right">
            {{ inbox.telegram_user_inbox_last_error }}
          </span>
        </div>
      </div>
    </SettingsSection>

    <SettingsSection
      :title="
        $t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_USER_INBOX.EGRESS_BIND_IP.LABEL')
      "
    >
      <div class="flex flex-col w-full max-w-3xl gap-3">
        <NextInput
          v-model="telegramUserEgressBindIp"
          :placeholder="
            $t(
              'INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_USER_INBOX.EGRESS_BIND_IP.PLACEHOLDER'
            )
          "
          :message="telegramUserEgressBindIpError"
          :message-type="telegramUserEgressBindIpError ? 'error' : 'info'"
        />
        <div>
          <NextButton
            :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
            @click="updateTelegramUserEgressBindIp"
          />
        </div>
      </div>
    </SettingsSection>

    <SettingsSection
      v-if="telegramUserReauthStep === 'code'"
      :title="$t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.TITLE')"
      class="mt-6"
    >
      <div class="flex flex-col w-full max-w-3xl gap-3">
        <p v-if="telegramUserPhoneNumberMasked" class="text-sm text-n-slate-11">
          {{ telegramUserPhoneNumberMasked }}
        </p>

        <form
          class="flex flex-col gap-4 flex-wrap mx-0"
          @submit.prevent="verifyTelegramUserReauthorizationCode"
        >
          <NextInput
            v-model="telegramUserCode"
            :label="$t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.CODE.LABEL')"
            :placeholder="
              $t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.CODE.PLACEHOLDER')
            "
            :message="telegramUserCodeError"
            :message-type="telegramUserCodeError ? 'error' : 'info'"
          />

          <div class="flex gap-2">
            <NextButton
              :is-loading="uiFlags.isVerifyingTelegramUserLogin"
              :label="$t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.VERIFY_CODE')"
              type="submit"
            />
            <NextButton
              :is-loading="uiFlags.isStartingTelegramUserLogin"
              :label="$t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.SEND_CODE')"
              type="button"
              @click="startTelegramUserReauthorization"
            />
          </div>
        </form>
      </div>
    </SettingsSection>
    <SettingsSection
      v-else-if="telegramUserReauthStep === 'password'"
      :title="$t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.TITLE')"
      class="mt-6"
    >
      <div class="flex flex-col w-full max-w-3xl gap-3">
        <p v-if="telegramUserPhoneNumberMasked" class="text-sm text-n-slate-11">
          {{ telegramUserPhoneNumberMasked }}
        </p>

        <form
          class="flex flex-col gap-4 flex-wrap mx-0"
          @submit.prevent="verifyTelegramUserReauthorizationPassword"
        >
          <NextInput
            v-model="telegramUserPassword"
            type="password"
            :label="$t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.PASSWORD.LABEL')"
            :placeholder="
              $t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.PASSWORD.PLACEHOLDER')
            "
            :message="telegramUserPasswordError"
            :message-type="telegramUserPasswordError ? 'error' : 'info'"
          />

          <div class="flex gap-2">
            <NextButton
              :is-loading="uiFlags.isVerifyingTelegramUserPassword"
              :label="
                $t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.SUBMIT_PASSWORD')
              "
              type="submit"
            />
            <NextButton
              :is-loading="uiFlags.isStartingTelegramUserLogin"
              :label="$t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.SEND_CODE')"
              type="button"
              @click="startTelegramUserReauthorization"
            />
          </div>
        </form>
      </div>
    </SettingsSection>
  </div>
  <div v-else-if="isAnEmailChannel">
    <div class="mx-8">
      <SettingsSection
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.FORWARD_EMAIL_TITLE')"
        :sub-title="
          isForwardingEnabled
            ? $t('INBOX_MGMT.SETTINGS_POPUP.FORWARD_EMAIL_SUB_TEXT')
            : ''
        "
      >
        <woot-code
          v-if="isForwardingEnabled"
          :script="inbox.forward_to_email"
        />
        <div
          v-else
          class="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg"
        >
          <p class="text-sm text-yellow-800 dark:text-yellow-200 mb-0">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.FORWARD_EMAIL_NOT_CONFIGURED') }}
          </p>
        </div>
      </SettingsSection>
    </div>
    <ImapSettings :inbox="inbox" />
    <SmtpSettings v-if="inbox.imap_enabled" :inbox="inbox" />
  </div>
  <div v-else-if="isAWhatsAppChannel && !isATwilioChannel">
    <div v-if="inbox.provider_config" class="mx-8">
      <!-- Embedded Signup Section -->
      <template v-if="isEmbeddedSignupWhatsApp">
        <SettingsSection
          v-if="whatsappAppId"
          :title="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_EMBEDDED_SIGNUP_TITLE')
          "
          :sub-title="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_EMBEDDED_SIGNUP_SUBHEADER')
          "
        >
          <div class="flex gap-4 items-center">
            <p class="text-sm text-n-slate-11">
              {{
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_EMBEDDED_SIGNUP_DESCRIPTION'
                )
              }}
            </p>
            <NextButton @click="handleReconfigure">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_RECONFIGURE_BUTTON') }}
            </NextButton>
          </div>
        </SettingsSection>
      </template>

      <!-- Manual Setup Section -->
      <template v-else>
        <SettingsSection
          :title="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_WEBHOOK_TITLE')"
          :sub-title="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_WEBHOOK_SUBHEADER')
          "
        >
          <woot-code :script="inbox.provider_config.webhook_verify_token" />
        </SettingsSection>
        <SettingsSection
          :title="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_TITLE')"
          :sub-title="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_SUBHEADER')
          "
        >
          <woot-code :script="inbox.provider_config.api_key" />
        </SettingsSection>
        <SettingsSection
          :title="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_TITLE')"
          :sub-title="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_SUBHEADER')
          "
        >
          <div
            class="flex flex-1 justify-between items-center mt-2 whatsapp-settings--content"
          >
            <woot-input
              v-model="whatsAppInboxAPIKey"
              type="text"
              class="flex-1 mr-2 [&>input]:!mb-0"
              :placeholder="
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_PLACEHOLDER'
                )
              "
            />
            <NextButton
              :disabled="v$.whatsAppInboxAPIKey.$invalid"
              @click="updateWhatsAppInboxAPIKey"
            >
              {{
                $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON')
              }}
            </NextButton>
          </div>
        </SettingsSection>
      </template>
      <SettingsSection
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_TITLE')"
        :sub-title="
          $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_SUBHEADER')
        "
      >
        <div class="flex justify-start items-center mt-2">
          <NextButton :disabled="isSyncingTemplates" @click="syncTemplates">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_BUTTON') }}
          </NextButton>
        </div>
      </SettingsSection>
    </div>
    <WhatsappReauthorize
      v-if="isEmbeddedSignupWhatsApp"
      ref="whatsappReauth"
      :inbox="inbox"
      class="hidden"
    />
  </div>
</template>

<style lang="scss" scoped>
.whatsapp-settings--content {
  ::v-deep input {
    margin-bottom: 0;
  }
}
</style>
